import { apiService } from './ApiService';
import { RepairEstimate, RepairType, CartItem } from './CartOrderService';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RepairEstimationPartInput {
  part_id: string;
  part_number?: string;
  description: string;
}

export interface LaborEstimateResult {
  part_id: string;
  labor_hours: number;
  labor_rate: number;
  repair_type: RepairType;
  labor_note: string;
}

export interface RepairEstimationApiResponse {
  estimates: LaborEstimateResult[];
  service_items: CartItem[];
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class RepairEstimationService {
  private static instance: RepairEstimationService;

  public static getInstance(): RepairEstimationService {
    if (!RepairEstimationService.instance) {
      RepairEstimationService.instance = new RepairEstimationService();
    }
    return RepairEstimationService.instance;
  }

  /**
   * Main entry point called by ChatPage's onEstimateRepairs prop.
   *
   * Calls the backend /api/user/repair-estimation/estimate endpoint (Gemini server-side).
   * 1. Filters eligible (non-service, found) parts.
   * 2. Attaches a `labor_estimate` to each matching cart item.
   * 3. Returns service cart items (is_service=true) already built by the backend.
   *
   * @returns updatedCartItems  — same array with labor_estimate fields filled in
   * @returns serviceItems      — CartItems with is_service=true to add to cart
   */
  public async estimateRepairsForCart(
    cartItems: any[],
    vinData?: any,
    laborRate: number = 85,
    locale?: string,
    i18nStrings?: {
      selectionReason?: string;
      repairTypeLabels?: Partial<Record<RepairType, string>>;
    }
  ): Promise<{
    updatedCartItems: any[];
    serviceItems: CartItem[];
  }> {
    const eligibleParts: RepairEstimationPartInput[] = cartItems
      .filter((item) => {
        const pd = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
        return !pd.not_found && !item.is_service && !pd.is_service;
      })
      .map((item) => {
        const pd = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
        return {
          part_id: pd.part_id || pd.PART_ID || item.part_id || '',
          part_number: pd.part_number || pd.PART_NUMBER,
          description: pd.description || pd.DESCRIPTION || '',
        };
      })
      .filter((p) => !!p.part_id);

    if (eligibleParts.length === 0) {
      return { updatedCartItems: cartItems, serviceItems: [] };
    }

    const response = await apiService.post<RepairEstimationApiResponse>(
      '/api/user/repair-estimation/estimate',
      {
        parts: eligibleParts,
        vin_data: vinData ?? null,
        labor_rate: laborRate,
        locale: locale ?? null,
        selection_reason:
          i18nStrings?.selectionReason ?? 'AI-generated repair labor estimation',
      }
    );

    const { estimates, service_items } = response.data;

    // Build a lookup map for quick access
    const estimatesMap = new Map<string, RepairEstimate>();
    for (const est of estimates) {
      estimatesMap.set(est.part_id, {
        labor_hours: est.labor_hours,
        labor_rate: est.labor_rate,
        repair_type: est.repair_type,
        labor_note: est.labor_note,
      });
    }

    // Attach labor_estimate to each matching cart item
    const updatedCartItems = cartItems.map((item) => {
      const pd = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
      const partId: string = pd.part_id || pd.PART_ID || item.part_id || '';
      if (!partId || !estimatesMap.has(partId)) return item;
      return this._setLaborEstimate(item, estimatesMap.get(partId)!);
    });

    // Optionally re-label service item descriptions with i18n repair-type labels
    const repairTypeLabels = i18nStrings?.repairTypeLabels;
    const serviceItems: CartItem[] = service_items.map((si: any) => {
      if (!repairTypeLabels) return si as CartItem;
      const pd = si.structured_data?.part_data || {};
      const repairType: RepairType | undefined = pd.labor_estimate?.repair_type;
      if (!repairType || !repairTypeLabels[repairType]) return si as CartItem;

      const localLabel = repairTypeLabels[repairType]!;
      const match = eligibleParts.find((p) => si.part_id?.includes(p.part_id));
      const partLabel = match?.description || match?.part_number || '';
      const newDescription = partLabel ? `${localLabel} – ${partLabel}` : pd.description;
      return {
        ...si,
        structured_data: {
          ...si.structured_data,
          part_data: { ...pd, description: newDescription },
        },
      } as CartItem;
    });

    return { updatedCartItems, serviceItems };
  }

  /**
   * Apply (or remove) a single manual labor estimate to a cart item.
   */
  public setLaborEstimateOnItem(item: any, estimate: RepairEstimate | null): any {
    return this._setLaborEstimate(item, estimate);
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _setLaborEstimate(item: any, estimate: RepairEstimate | null): any {
    if (item.structured_data?.part_data) {
      return {
        ...item,
        structured_data: {
          ...item.structured_data,
          part_data: { ...item.structured_data.part_data, labor_estimate: estimate ?? undefined },
        },
      };
    }
    if (item.structured_data?.PART_DATA) {
      return {
        ...item,
        structured_data: {
          ...item.structured_data,
          PART_DATA: { ...item.structured_data.PART_DATA, labor_estimate: estimate ?? undefined },
        },
      };
    }
    return item;
  }
}

export const repairEstimationService = RepairEstimationService.getInstance();
